import {
  APIFieldAttributes,
  APIFieldType,
  PartiallyPersistedConditionalQuestion,
  PartiallyPersistedReferenceQuestion,
  PartiallyPersistedChildQuestion,
  PersistedFieldSubQuestion,
  PersistedReferenceSubQuestion,
  PersistedSurveyQuestion,
  SurveyQuestionType,
  UnpersistedFieldSubQuestion,
  UnpersistedReferenceSubQuestion,
  UnpersistedSurveyQuestion,
} from '@ardoq/api-types';
import {
  dateRangeOperations,
  getStartDateName,
  getDateRangeType,
} from '@ardoq/date-range';
import { Fields } from '../types';
import {
  isAttributeQuestion,
  isDateRangeQuestion,
  isFieldQuestion,
  isHiddenQuestion,
} from '../questions/questionPredicates';

type AnySurveyQuestion =
  | PersistedSurveyQuestion
  | UnpersistedSurveyQuestion
  | PartiallyPersistedConditionalQuestion
  | PartiallyPersistedReferenceQuestion
  | PartiallyPersistedChildQuestion;

export const getFieldType = (
  fieldName: string,
  fields: Fields
): APIFieldType | undefined => {
  if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
    const field = fields[getStartDateName(fieldName)];

    return field ? getDateRangeType(field.type) : undefined;
  }

  const field = fields[fieldName];

  return field ? field.type : undefined; // If a field has been deleted, type should be undefined
};

export const getFieldTypeFromAttributeName = (
  attributeName: string
): APIFieldType => {
  if (attributeName === 'description') {
    return APIFieldType.TEXT_AREA;
  }
  // TODO: What about parent questions?
  return APIFieldType.TEXT;
};

export const getFieldName = (
  question: PersistedSurveyQuestion
): string | [string, string] | null => {
  if (isAttributeQuestion(question)) {
    return question.properties.attributeName;
  }
  if (isFieldQuestion(question) || isHiddenQuestion(question)) {
    const fieldName = question.properties.fieldName;
    if (
      isDateRangeQuestion(question) &&
      dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)
    ) {
      return dateRangeOperations.maybeExpandDateRangeFieldName(fieldName);
    }
    return fieldName;
  }
  return null;
};

export const getDateRangeStartAndEndDateFields = (
  question: PersistedSurveyQuestion,
  fields: Fields
) => {
  if (
    question.type !== SurveyQuestionType.FIELD &&
    question.type !== SurveyQuestionType.HIDDEN_FIELD
  )
    return;
  const [startDateFieldName, endDateFieldName] =
    question.properties.fieldName.split(',');
  return {
    startDate: fields[startDateFieldName],
    endDate: fields[endDateFieldName],
  };
};

export const getFieldMapByName = (fields: APIFieldAttributes[]): Fields => {
  return fields.reduce((acc, field) => ({ ...acc, [field.name]: field }), {});
};

const getFieldNamesFromReferenceSubQuestion = (
  subQuestion: PersistedReferenceSubQuestion | UnpersistedReferenceSubQuestion
): string[] => {
  if (subQuestion.type === SurveyQuestionType.FIELD) {
    const fieldName = subQuestion.properties.fieldName;
    if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
      const expandedNames =
        dateRangeOperations.maybeExpandDateRangeFieldName(fieldName);
      return expandedNames ? [...expandedNames] : [fieldName];
    }
    return [fieldName];
  }
  return [];
};

const getFieldNamesFromFieldSubQuestion = (
  subQuestion: PersistedFieldSubQuestion | UnpersistedFieldSubQuestion
): string[] => {
  if (subQuestion.type === SurveyQuestionType.FIELD) {
    const fieldName = subQuestion.properties.fieldName;
    if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
      const expandedNames =
        dateRangeOperations.maybeExpandDateRangeFieldName(fieldName);
      return expandedNames ? [...expandedNames] : [fieldName];
    }
    return [fieldName];
  }
  if (subQuestion.type === SurveyQuestionType.REFERENCE) {
    return (subQuestion.properties.questions ?? []).flatMap(
      getFieldNamesFromReferenceSubQuestion
    );
  }
  return [];
};

export const getFieldNames = (question: AnySurveyQuestion): string[] => {
  if (question.type === SurveyQuestionType.FIELD) {
    const subQuestionFieldNames =
      'questions' in question.properties &&
      Array.isArray(question.properties.questions)
        ? question.properties.questions.flatMap(
            getFieldNamesFromFieldSubQuestion
          )
        : [];
    const fieldName = question.properties.fieldName;
    if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
      const expandedNames =
        dateRangeOperations.maybeExpandDateRangeFieldName(fieldName);
      return expandedNames
        ? [...expandedNames, ...subQuestionFieldNames]
        : [fieldName, ...subQuestionFieldNames];
    }
    return [fieldName, ...subQuestionFieldNames];
  }
  if (question.type === SurveyQuestionType.HIDDEN_FIELD) {
    const fieldName = question.properties.fieldName;
    if (dateRangeOperations.isCombinedDateRangeFieldNames(fieldName)) {
      const expandedNames =
        dateRangeOperations.maybeExpandDateRangeFieldName(fieldName);
      return expandedNames ? [...expandedNames] : [fieldName];
    }
    return [fieldName];
  }
  if (question.type === SurveyQuestionType.REFERENCE) {
    return (question.properties.questions ?? []).flatMap(
      getFieldNamesFromReferenceSubQuestion
    );
  }
  return [];
};
